import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import SecurityHeader from '../../components/ui/SecurityHeader';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NotificationBanner from '../../components/ui/NotificationBanner';
import SecurityMetricsCard from './components/SecurityMetricsCard';
import AccessSuccessChart from './components/AccessSuccessChart';
import DoorAccessChart from './components/DoorAccessChart';
import BehavioralAnalysisPanel from './components/BehavioralAnalysisPanel';
import AnalyticsFilters from './components/AnalyticsFilters';

const Analytics = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: 'last30days',
    doorFilter: 'all',
    userGroup: 'all',
    timeRange: 'all'
  });

  // Mock analytics data
  const [analyticsData, setAnalyticsData] = useState({
    metrics: {
      totalAccess: 2847,
      successRate: 94.2,
      anomalies: 23,
      avgResponseTime: 1.2
    },
    accessSuccess: {
      successful: 2683,
      failed: 164,
      denied: 47
    },
    doorAccess: [
      { door: 'Main Entrance', successful: 1245, failed: 67 },
      { door: 'Server Room', successful: 234, failed: 12 },
      { door: 'Office A', successful: 456, failed: 23 },
      { door: 'Office B', successful: 389, failed: 18 },
      { door: 'Conference Room', successful: 359, failed: 44 }
    ],
    behavioralAnalysis: {
      peakHours: '9:00 AM - 11:00 AM',
      anomalyRate: 3.2,
      offHoursAccess: 8,
      multipleAttempts: 4
    }
  });

  useEffect(() => {
    // Simulate data loading
    const loadData = async () => {
      setIsLoading(true);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update data based on filters (mock implementation)
      const updatedData = { ...analyticsData };
      
      // Apply filter-based modifications to mock data
      if (filters?.dateRange === 'today') {
        updatedData.metrics.totalAccess = Math.floor(analyticsData?.metrics?.totalAccess * 0.1);
        updatedData.accessSuccess.successful = Math.floor(analyticsData?.accessSuccess?.successful * 0.1);
        updatedData.accessSuccess.failed = Math.floor(analyticsData?.accessSuccess?.failed * 0.1);
        updatedData.accessSuccess.denied = Math.floor(analyticsData?.accessSuccess?.denied * 0.1);
      } else if (filters?.dateRange === 'last7days') {
        updatedData.metrics.totalAccess = Math.floor(analyticsData?.metrics?.totalAccess * 0.25);
        updatedData.accessSuccess.successful = Math.floor(analyticsData?.accessSuccess?.successful * 0.25);
        updatedData.accessSuccess.failed = Math.floor(analyticsData?.accessSuccess?.failed * 0.25);
        updatedData.accessSuccess.denied = Math.floor(analyticsData?.accessSuccess?.denied * 0.25);
      }

      setAnalyticsData(updatedData);
      setIsLoading(false);
    };

    loadData();
  }, [filters]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const metricsCards = [
    {
      title: 'Total Access Attempts',
      value: analyticsData?.metrics?.totalAccess?.toLocaleString(),
      change: '+12.5%',
      changeType: 'positive',
      icon: 'Activity',
      description: 'All access attempts across all doors'
    },
    {
      title: 'Success Rate',
      value: `${analyticsData?.metrics?.successRate}%`,
      change: '+2.1%',
      changeType: 'positive',
      icon: 'CheckCircle',
      description: 'Percentage of successful access attempts'
    },
    {
      title: 'Security Anomalies',
      value: analyticsData?.metrics?.anomalies?.toString(),
      change: '-8.3%',
      changeType: 'positive',
      icon: 'AlertTriangle',
      description: 'Detected unusual access patterns'
    },
    {
      title: 'Avg Response Time',
      value: `${analyticsData?.metrics?.avgResponseTime}s`,
      change: '-0.2s',
      changeType: 'positive',
      icon: 'Zap',
      description: 'Average system response time'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Analytics - SecureLock Admin</title>
        <meta name="description" content="Comprehensive security analytics and reporting dashboard for access control system performance monitoring" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <SecurityHeader />
        <NotificationBanner />
        
        <main className="pt-16 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground">Security Analytics</h1>
              <p className="text-muted-foreground mt-2">
                Comprehensive analysis of access control patterns and security performance metrics
              </p>
            </div>

            {/* Analytics Filters */}
            <AnalyticsFilters 
              onFiltersChange={handleFiltersChange}
              isLoading={isLoading}
            />

            {/* Key Performance Indicators */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {metricsCards?.map((metric, index) => (
                <SecurityMetricsCard
                  key={index}
                  title={metric?.title}
                  value={metric?.value}
                  change={metric?.change}
                  changeType={metric?.changeType}
                  icon={metric?.icon}
                  description={metric?.description}
                />
              ))}
            </div>

            {/* Main Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <AccessSuccessChart 
                data={analyticsData?.accessSuccess}
                isLoading={isLoading}
              />
              
              <DoorAccessChart 
                data={analyticsData?.doorAccess}
                isLoading={isLoading}
              />
            </div>

            {/* Behavioral Analysis Panel */}
            <BehavioralAnalysisPanel 
              data={analyticsData?.behavioralAnalysis}
              isLoading={isLoading}
            />

            {/* Additional Insights Section */}
            <div className="mt-8 bg-card border border-border rounded-lg p-6 security-shadow-card">
              <h3 className="text-lg font-semibold text-foreground mb-4">Security Recommendations</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-success rounded-full mt-2" />
                    <div>
                      <h4 className="text-sm font-medium text-foreground">Strong Performance</h4>
                      <p className="text-sm text-muted-foreground">
                        Overall access success rate of 94.2% indicates well-functioning system
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-warning rounded-full mt-2" />
                    <div>
                      <h4 className="text-sm font-medium text-foreground">Monitor Conference Room</h4>
                      <p className="text-sm text-muted-foreground">
                        Higher failure rate detected - consider maintenance check
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-accent rounded-full mt-2" />
                    <div>
                      <h4 className="text-sm font-medium text-foreground">Peak Hour Optimization</h4>
                      <p className="text-sm text-muted-foreground">
                        Consider load balancing during 9-11 AM peak access times
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-success rounded-full mt-2" />
                    <div>
                      <h4 className="text-sm font-medium text-foreground">Low Anomaly Rate</h4>
                      <p className="text-sm text-muted-foreground">
                        3.2% anomaly rate is within acceptable security parameters
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <BottomTabNavigation />
      </div>
    </>
  );
};

export default Analytics;